import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { AnimatePresence } from 'framer-motion';
import { useMusic } from '../../context/MusicContext';
import { 
  Overlay, PaletteContainer, SearchInput, 
  CommandList, CommandItem, CommandGroup, MagicInk
} from './CommandPalette.styles';

const CommandPalette = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [selectedIndex, setSelectedIndex] = useState(0); 
  const [hasCopied, setHasCopied] = useState(false);
  
  const [inkStyle, setInkStyle] = useState({ top: 0, height: 0, opacity: 0 });
  const listRef = useRef(null);
  
  const inputRef = useRef(null);
  const previousFocusRef = useRef(null); 
  
  // THE FIX: Ref to track true physical mouse movement
  const lastMousePos = useRef({ x: 0, y: 0 });
  
  const navigate = useNavigate();
  const { executeMusicCommand } = useMusic();

  useEffect(() => {
    const handleGlobalKeyDown = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault(); 
        setIsOpen((prev) => !prev);
      }
    };

    window.addEventListener('keydown', handleGlobalKeyDown);
    return () => window.removeEventListener('keydown', handleGlobalKeyDown);
  }, []);

  useEffect(() => {
    if (isOpen) {
      previousFocusRef.current = document.activeElement;
      setTimeout(() => inputRef.current?.focus(), 50);
      setSearch('');
      setSelectedIndex(0); 
      setHasCopied(false);
    } else {
      if (previousFocusRef.current) {
        previousFocusRef.current.focus();
      }
      setInkStyle({ opacity: 0 }); 
    }
  }, [isOpen]);

  const commands = [
    { id: 'dev', group: 'Navigation', title: 'Go to Dev Environment', action: () => navigate('/') },
    { id: 'art', group: 'Navigation', title: 'Go to Art Gallery', action: () => navigate('/art') },
    { id: 'github', group: 'External', title: 'Open GitHub', action: () => window.open('https://github.com/darin-noronha', '_blank') },
    { id: 'linkedin', group: 'External', title: 'Open LinkedIn', action: () => window.open('https://linkedin.com/in/darin-noronha', '_blank') },
    { id: 'play-music', group: 'Media', title: 'Play Music', action: () => executeMusicCommand('music') },
    { id: 'stop-music', group: 'Media', title: 'Stop Music', action: () => executeMusicCommand('end music') },
    { 
      id: 'email', 
      group: 'Contact', 
      title: hasCopied ? 'email copied!' : 'Copy Email Address', 
      keepOpen: true, 
      action: () => {
        navigator.clipboard.writeText('dnoronha@umich.edu');
        setHasCopied(true);
        setTimeout(() => {
          setHasCopied(false);
          setIsOpen(false);
        }, 1200); 
      } 
    },
  ];

  const filteredCommands = commands.filter(cmd => 
    cmd.title.toLowerCase().includes(search.toLowerCase()) || 
    cmd.group.toLowerCase().includes(search.toLowerCase())
  );

  useEffect(() => {
    setSelectedIndex(0);
  }, [search]);

  useEffect(() => {
    const activeItem = document.getElementById('command-item-active');
    
    if (activeItem && listRef.current) {
      setInkStyle({
        top: activeItem.offsetTop,
        height: activeItem.offsetHeight,
        opacity: 1
      });
      activeItem.scrollIntoView({ block: 'nearest' });
    } else {
      setInkStyle({ opacity: 0 });
    }
  }, [selectedIndex, filteredCommands.length, isOpen]);

  const executeCommand = (cmd) => {
    cmd.action();
    if (!cmd.keepOpen) {
      setIsOpen(false);
    }
  };

  const handleInputKeyDown = (e) => {
    if (e.key === 'Escape') {
      setIsOpen(false);
    } else if (e.key === 'ArrowDown') {
      e.preventDefault();
      setSelectedIndex((prev) => Math.min(prev + 1, filteredCommands.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setSelectedIndex((prev) => Math.max(prev - 1, 0));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      if (filteredCommands.length > 0) {
        executeCommand(filteredCommands[selectedIndex]);
      }
    }
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <Overlay
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          onClick={() => setIsOpen(false)}
        >
          <PaletteContainer
            onClick={(e) => e.stopPropagation()}
            initial={{ scale: 0.95, opacity: 0, y: -20 }}
            animate={{ scale: 1, opacity: 1, y: 0 }}
            exit={{ scale: 0.95, opacity: 0, y: -20 }}
            transition={{ type: 'spring', stiffness: 300, damping: 25 }}
          >
            <SearchInput 
              ref={inputRef}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              onKeyDown={handleInputKeyDown} 
              placeholder="Type a command or search..."
              spellCheck="false"
            />
            
            <CommandList ref={listRef}>
              <MagicInk {...inkStyle} />
              
              {filteredCommands.length === 0 ? (
                <CommandGroup style={{ textAlign: 'center', padding: '2rem' }}>No commands found.</CommandGroup>
              ) : (
                filteredCommands.map((cmd, index) => {
                  const isActive = index === selectedIndex;
                  return (
                    <CommandItem 
                      key={cmd.id}
                      id={isActive ? 'command-item-active' : undefined}
                      $active={isActive}
                      onClick={() => executeCommand(cmd)}
                      
                      // THE FIX: Only update hover state if the mouse actually moved pixels
                      onMouseMove={(e) => {
                        if (e.clientX !== lastMousePos.current.x || e.clientY !== lastMousePos.current.y) {
                          lastMousePos.current = { x: e.clientX, y: e.clientY };
                          setSelectedIndex(index);
                        }
                      }}
                    >
                      <span style={{ color: (cmd.id === 'email' && hasCopied) ? '#9ece6a' : 'inherit', transition: 'color 0.2s' }}>
                        {cmd.title}
                      </span>
                      <span style={{ fontSize: '0.8rem', color: isActive ? '#a9b1d6' : '#565f89', transition: 'color 0.2s' }}>
                        {cmd.group}
                      </span>
                    </CommandItem>
                  );
                })
              )}
            </CommandList>
          </PaletteContainer>
        </Overlay>
      )}
    </AnimatePresence>
  );
};

export default CommandPalette;